import React from 'react';
import { InsuranceType, SelectedProgramsByTravellers, useSaveOrderServicesMutation } from '@websky/graphql';
import { IINFieldKeyFactory } from './utils';
import { fillOrder } from '../Checkout/store/order/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useToggleable } from '../hooks';
import { getOrder as getRootOrder } from '../Checkout/store/order/selectors';
import { InsuranceIINModalValues } from './components/InsuranceModalConfirmINN';

export const usePassengersToConfirmIIN = (setNextStep: React.DispatchWithoutAction) => {
	const dispatch = useDispatch();

	const order = useSelector(getRootOrder);

	const [saveOrderServices, saveOrderServicesResult] = useSaveOrderServicesMutation();
	const { isOpen: isInsuranceModalOpen, open: openInsuranceModal, close: closeInsuranceModal } = useToggleable(false);

	const insurances = order.additionalServices.insurances;

	const allowedPassengers = insurances?.insurancePrograms.find(
		program => program.type === InsuranceType.AlphaInsurance
	)?.allowedPassengers;
	const isPassengerIinNeeded = insurances?.travellerExtraValues.some(extraValue => extraValue.needKzIinDocument);
	const isAlphaInsuranceSelected = insurances?.selectedInsurances.find(
		selectedInsurance => selectedInsurance.insuranceProgram.type === InsuranceType.AlphaInsurance
	);

	const filteredPassengers = order.travellers.filter(traveller => allowedPassengers?.includes(Number(traveller.id)));

	const passengersToConfirmIIN =
		!!isAlphaInsuranceSelected && isPassengerIinNeeded && !!filteredPassengers.length ? filteredPassengers : [];

	const isNeededToConfirmIIN = !!passengersToConfirmIIN.length;

	const saveInsuranceAndUpdateOrder = async (values: InsuranceIINModalValues) => {
		const selectedPrograms = insurances?.selectedInsurances?.map(
			selectedInsurance => selectedInsurance.insuranceProgram.code
		);

		const programsByTravellers: SelectedProgramsByTravellers[] = filteredPassengers.map(traveller => {
			const travellerIINKey = IINFieldKeyFactory(traveller.id);

			return {
				travellerId: traveller.id,
				programCodes: selectedPrograms,
				travellerDocumentNumber: values[travellerIINKey]
			};
		});

		try {
			const { data } = await saveOrderServices({
				variables: {
					params: {
						id: order.id,
						insurances: [
							{
								insuranceType: InsuranceType.AlphaInsurance,
								selectedPrograms,
								selectedProgramsByTravellers: programsByTravellers
							}
						]
					}
				}
			});

			if (data?.SaveOrderServices) {
				dispatch(fillOrder(data.SaveOrderServices));

				closeInsuranceModal();
				setNextStep();
			}
		} catch (error) {
			return error['graphQLErrors'][0].debugMessage;
		}
	};

	return {
		passengersToConfirmIIN,
		isNeededToConfirmIIN,
		isInsuranceModalOpen,
		openInsuranceModal,
		closeInsuranceModal,
		isSaveOrderServicesLoading: saveOrderServicesResult.loading,
		saveInsuranceAndUpdateOrder
	};
};
